export interface PageMeta {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
}

// Client-side fallback meta only. These values are used when the server has
// not injected SSR meta tags (e.g. client-side navigation) AND no CMS
// override exists in the seo_meta table. Intentionally free of volatile
// business data (founding year, phone number, street address): that data is
// CMS-managed (site_settings / seo_meta) and editable from the CRM under
// SEO & Marketing → SEO Pagine.
export const pageMeta: Record<string, PageMeta> = {
  '/': {
    title: 'UNIVERSO ORO - Compro Oro Roma, Banco Metalli - Gioielleria - Orologeria',
    description: 'Leader a Roma nella compravendita oro, argento, orologi di lusso e lingotti da investimento. Valutazioni gratuite, massime quotazioni garantite.',
    keywords: ['compro oro roma', 'banco metalli roma', 'vendere oro', 'orologi lusso usati', 'quotazioni oro oggi', 'compro argento roma'],
  },
  '/banco-metalli-roma-professionisti': {
    title: 'Banco Metalli Roma - Quotazioni Oro e Argento per Professionisti',
    description: 'Servizio banco metalli per operatori del settore. Fusione oro, recupero metalli preziosi, quotazioni wholesale. Spread competitivi per grandi volumi.',
    keywords: ['banco metalli roma', 'quotazioni oro professionisti', 'fusione oro roma', 'recupero metalli preziosi'],
  },
  '/vendere-comprare-orologi-lusso-roma': {
    title: 'Vendere Orologi di Lusso a Roma - Rolex, Patek Philippe, Audemars Piguet',
    description: 'Compravendita orologi di lusso usati a Roma. Valutazione gratuita Rolex, Patek Philippe, Omega. Pagamento immediato, massima serietà.',
    keywords: ['vendere rolex roma', 'compro orologi lusso roma', 'valutazione patek philippe', 'orologi usati roma'],
  },
  '/compro-oro-argento-roma-valutazione': {
    title: 'Compro Oro Roma - Valutazione Gratuita e Pagamento Immediato',
    description: 'Valutazione gratuita oro e argento a Roma. Quotazioni trasparenti, pagamento immediato. Leader nel settore con migliaia di clienti soddisfatti.',
    keywords: ['compro oro roma', 'valutazione oro gratuita', 'vendere oro usato', 'quotazione oro oggi roma'],
  },
  '/lingotti-oro-monete-investimento': {
    title: 'Lingotti Oro da Investimento - Monete e Lingotti Certificati',
    description: 'Vendita lingotti oro e monete da investimento. Solo prodotti certificati LBMA. Consulenza gratuita per investimenti in oro fisico.',
    keywords: ['lingotti oro investimento', 'comprare oro fisico', 'monete oro investimento', 'investire in oro'],
  },
  '/chi-siamo': {
    title: 'Chi Siamo - UNIVERSO ORO, Esperienza e Tradizione a Roma',
    description: 'La storia di UNIVERSO ORO, tradizione familiare nel settore dei metalli preziosi. Professionalità, trasparenza e competenza a Roma.',
    keywords: ['universo oro roma', 'compro oro affidabile roma', 'storia universo oro'],
  },
  '/dove-siamo': {
    title: 'Dove Siamo - UNIVERSO ORO Roma',
    description: 'Scopri dove si trova UNIVERSO ORO a Roma: sede facilmente raggiungibile, ampio parcheggio disponibile. Aperti anche il sabato mattina.',
    keywords: ['universo oro dove siamo', 'negozio oro roma nord', 'compro oro roma indirizzo'],
  },
  '/contatti': {
    title: 'Contatti - UNIVERSO ORO Roma',
    description: 'Contatta UNIVERSO ORO per valutazioni gratuite di oro, argento e orologi di lusso. Rispondiamo rapidamente a tutte le richieste.',
    keywords: ['contatti universo oro', 'numero telefono compro oro roma', 'email universo oro'],
  },
};

/**
 * Enrich the static page meta with CMS-driven company data
 * (founding year, phone, address). Values are injected only when the
 * corresponding CMS key is present — absent keys degrade gracefully to
 * the neutral static copy above.
 */
function enrichWithSettings(pathname: string, meta: PageMeta, settings?: Record<string, string>): PageMeta {
  if (!settings) return meta;

  if (pathname === '/chi-siamo') {
    const year = (settings.founding_year || '').trim();
    if (/^\d{4}$/.test(year)) {
      const years = Math.max(new Date().getFullYear() - parseInt(year, 10), 1);
      return {
        ...meta,
        title: `Chi Siamo - UNIVERSO ORO, ${years} Anni di Esperienza a Roma`,
        description: `La storia di UNIVERSO ORO, tradizione familiare nel settore dei metalli preziosi dal ${year}. Professionalità, trasparenza e competenza a Roma.`,
      };
    }
    return meta;
  }

  if (pathname === '/dove-siamo') {
    const address = (settings.address_rome || '').trim();
    if (address) {
      return {
        ...meta,
        title: `Dove Siamo - UNIVERSO ORO Roma, ${address.split(',')[0].trim()}`,
        description: `UNIVERSO ORO si trova a Roma in ${address}. Facilmente raggiungibile, ampio parcheggio disponibile. Aperti anche il sabato mattina.`,
      };
    }
    return meta;
  }

  if (pathname === '/contatti') {
    const phone = (settings.phone_main || '').trim();
    if (phone) {
      return {
        ...meta,
        title: `Contatti - UNIVERSO ORO Roma | Tel: ${phone}`,
        description: `Contatta UNIVERSO ORO per valutazioni gratuite oro e argento. Tel: ${phone}. Rispondiamo rapidamente a tutte le richieste.`,
      };
    }
    return meta;
  }

  return meta;
}

export function getPageMeta(pathname: string, settings?: Record<string, string>): PageMeta | undefined {
  // First check for exact match
  if (pageMeta[pathname]) {
    return enrichWithSettings(pathname, pageMeta[pathname], settings);
  }
  
  // Check for old service URLs and return new URL meta
  const oldToNewUrlMap: Record<string, string> = {
    '/servizio/banco-metalli': '/banco-metalli-roma-professionisti',
    '/servizio/orologeria': '/vendere-comprare-orologi-lusso-roma',
    '/servizio/compro-oro': '/compro-oro-argento-roma-valutazione',
    '/servizio/investimenti': '/lingotti-oro-monete-investimento',
  };
  
  if (oldToNewUrlMap[pathname]) {
    return pageMeta[oldToNewUrlMap[pathname]];
  }
  
  // Only the homepage gets the default meta. Unknown routes return undefined
  // so that pages managing their own SEO (SeoManager / useSeo) are not
  // clobbered with the generic homepage title by the layout-level MetaTags.
  return undefined;
}
